import React, { useRef, useCallback } from 'react';
import { Square, Music, Check, Play, Pause, Loader2, Plus } from 'lucide-react';
import { useAiLiteProperties } from '../../hooks/useAiLiteProperties';
import { getImageSlots, getImageRoleLabel } from '../../config/aiModels';
import MediaSelectorModal from '../Canvas/PropertiesPanel/MediaSelectorModal';

/**
 * AiLiteFields — renders AI layer properties in the lite view's "Fields" section.
 *
 * Layout (compact, unified block):
 *   ┌──────────────────────────────────────────────────────┐
 *   │  [ref][ref][ref]  │  prompt textarea                 │
 *   │   2/3 references  │  uses N credits       [Generate] │
 *   └──────────────────────────────────────────────────────┘
 *   │  [gen1][gen2]  Show all generations                  │
 *
 * - References: slot-based thumbnails for the current generation
 * - Prompt: editable
 * - Model: locked (not changeable) to preserve template style
 */
const AiLiteFields = ({ layer }) => {
  const element = layer?.element;
  const mediaSelectorRef = useRef(null);

  const {
    prompt,
    setPrompt,
    referenceMediaList,
    isGenerating,
    generationError,
    jobStatus,
    activeUrl,
    lockedModel,
    creditCost,
    canAfford,
    job,
    results,
    historyJobs,
    showingAllHistory,
    swappingKey,
    playingKey,
    setPlayingKey,
    handleGenerate,
    stopGeneration,
    handleSelectResult,
    handleAddReference,
    handleRemoveReference,
    fetchAllHistory,
    collapseHistory,
  } = useAiLiteProperties(element);

  // Open media selector for adding / swapping a reference image
  const handleAddReferenceClick = useCallback(() => {
    mediaSelectorRef.current?.open('image', null, (result, name) => {
      const url = typeof result === 'string' ? result : result?.url;
      const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
      if (url) handleAddReference(url, name, thumbnailUrl);
    });
  }, [handleAddReference]);

  // Swap an existing reference image at a specific index
  const handleSwapReference = useCallback((index) => {
    mediaSelectorRef.current?.open('image', referenceMediaList[index]?.url, (result, name) => {
      const url = typeof result === 'string' ? result : result?.url;
      const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
      if (url) {
        // Remove old one and add new one in its place
        handleRemoveReference(index);
        handleAddReference(url, name, thumbnailUrl);
      }
    });
  }, [referenceMediaList, handleRemoveReference, handleAddReference]);

  if (!element || element.type !== 'ai') return null;

  const isQueued = jobStatus === 'pending';
  const isProcessing = jobStatus === 'processing';
  const currentJobId = job?._id;

  // Merge current + history results
  const allResults = [
    ...results.map((r, i) => ({ ...r, _jobId: currentJobId, _idx: i })),
    ...historyJobs.flatMap((hj) =>
      (hj.results || []).map((r, i) => ({ ...r, _jobId: hj._id, _idx: i }))
    ),
  ];

  const imageSlots = getImageSlots(lockedModel);
  const totalSlots = imageSlots.reduce((sum, s) => sum + (s.max || 1), 0);
  const filledSlots = referenceMediaList.length;

  return (
    <div className="space-y-2">
      {/* Generation error */}
      {generationError && (
        <div className="rounded bg-red-900/30 border border-red-700/50 p-2 text-[11px] font-sourceSans text-red-300 text-center">
          Generation failed. Try again
        </div>
      )}

      {/* Generating status */}
      {(isQueued || isProcessing) && (
        <div className="flex items-center gap-2 bg-ag-black-600 rounded-lg px-3 py-1.5">
          <div className="w-3 h-3 border-2 border-ag-green border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="text-[11px] font-sourceSans text-white flex-1">
            {isQueued ? 'Queued...' : 'Generating...'}
          </span>
          <button
            onClick={stopGeneration}
            className="flex items-center gap-1 px-2 py-0.5 rounded bg-ag-black-500 hover:bg-red-900/40 text-[10px] font-sourceSans text-ag-black-300 hover:text-red-300 transition-colors"
          >
            <Square size={8} className="fill-current" />
            Stop
          </button>
        </div>
      )}

      {/* Unified block: references + prompt + generate */}
      <div className="flex items-stretch gap-2">
        {/* Reference thumbnails */}
        {totalSlots > 0 && (
          <div className="flex flex-col gap-1 flex-shrink-0">
            <div className="flex gap-1 items-start flex-wrap">
              {imageSlots.flatMap((slot) => {
                const max = slot.max || 1;
                const matchedRefs = referenceMediaList
                  .map((m, i) => ({ ...m, _origIdx: i }))
                  .filter(m => m.role === slot.role);
                const items = [];

                // Render each filled reference for this slot
                matchedRefs.forEach((ref, i) => {
                  items.push(
                    <ReferenceSlot
                      key={`${slot.role}-${i}`}
                      slot={slot}
                      media={ref}
                      lockedModel={lockedModel}
                      onSwap={() => handleSwapReference(ref._origIdx)}
                      onRemove={() => handleRemoveReference(ref._origIdx)}
                      isGenerating={isGenerating}
                    />
                  );
                });

                // If there's capacity for more, show an empty add button
                if (matchedRefs.length < max) {
                  items.push(
                    <ReferenceSlot
                      key={`${slot.role}-empty`}
                      slot={slot}
                      media={null}
                      lockedModel={lockedModel}
                      onSwap={handleAddReferenceClick}
                      onRemove={null}
                      isGenerating={isGenerating}
                    />
                  );
                }

                return items;
              })}
            </div>
            <span className="text-[9px] font-sourceSans text-ag-black-400 text-center">
              {filledSlots}/{totalSlots} references
            </span>
          </div>
        )}

        {/* Prompt + bottom controls */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isGenerating) {
                e.preventDefault();
                handleGenerate();
              }
            }}
            placeholder={isGenerating ? 'Generating...' : 'Edit prompt or regenerate...'}
            disabled={isGenerating}
            rows={2}
            className={`w-full bg-ag-black-800 text-xs font-sourceSans text-white placeholder-gray-500 rounded-lg border border-ag-black-400 focus:border-ag-green focus:outline-none p-2 resize-none ${
              isGenerating ? 'opacity-30 cursor-not-allowed' : ''
            }`}
          />

          {/* Credits + Generate inline */}
          <div className="flex items-center gap-2 justify-end">
            <div className="flex-1" />
            {!isGenerating && creditCost != null && (
              <span className={`text-[10px] font-sourceSans whitespace-nowrap ${canAfford ? 'text-ag-black-400' : 'text-red-400'}`}>
                uses {creditCost} {creditCost === 1 ? 'credit' : 'credits'}
              </span>
            )}
            {isGenerating ? (
              <button
                onClick={stopGeneration}
                className="flex-shrink-0 px-3 py-1 rounded-lg bg-red-600 hover:bg-red-500 text-white text-[11px] font-sourceSans font-medium transition-colors"
              >
                Stop
              </button>
            ) : canAfford ? (
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim()}
                className="flex-shrink-0 px-3 py-1 rounded-lg bg-ag-green hover:bg-ag-green/90 text-black text-[11px] font-sourceSans font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Generate
              </button>
            ) : (
              <a
                href="/manage-subscription"
                className="flex-shrink-0 px-3 py-1 bg-red-600 text-white text-[11px] font-sourceSans font-semibold rounded-lg hover:bg-red-500 transition-colors"
              >
                Upgrade
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Previous generations */}
      {allResults.length > 0 && (
        <div>
          <div className="flex gap-1.5 flex-wrap">
            {allResults.map((r) => (
              <AiResultThumbnail
                key={`${r._jobId}-${r._idx}`}
                result={r}
                jobId={r._jobId}
                resultIndex={r._idx}
                isActive={activeUrl && r.url === activeUrl}
                onSelect={handleSelectResult}
                isSwapping={swappingKey === `${r._jobId}-${r._idx}`}
                playingKey={playingKey}
                onPlay={setPlayingKey}
              />
            ))}
          </div>
          <div className="mt-1">
            {!showingAllHistory ? (
              <button onClick={fetchAllHistory} className="text-[10px] font-sourceSans text-ag-black-400 hover:text-ag-green transition-colors">
                Show all generations
              </button>
            ) : (
              <button onClick={collapseHistory} className="text-[10px] font-sourceSans text-ag-black-400 hover:text-ag-green transition-colors">
                Show less
              </button>
            )}
          </div>
        </div>
      )}

      <MediaSelectorModal ref={mediaSelectorRef} />
    </div>
  );
};

/**
 * ReferenceSlot — shows a single reference image slot (filled or empty).
 * Clicking a filled slot opens the media selector to swap it.
 */
const ReferenceSlot = ({ slot, media, lockedModel, onSwap, onRemove, isGenerating }) => (
  <div className="relative group flex flex-col items-center">
    <button
      onClick={!isGenerating ? onSwap : undefined}
      disabled={isGenerating}
      className={`w-14 h-14 rounded-md overflow-hidden border transition-colors flex-shrink-0 ${
        media
          ? 'border-ag-black-400 hover:border-ag-green'
          : 'border-dashed border-ag-black-400 hover:border-ag-green bg-ag-black-600'
      } ${isGenerating ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      title={media ? `Click to swap ${slot.label}` : `Add ${slot.label}`}
    >
      {media ? (
        <img
          crossOrigin="anonymous"
          src={media.thumbnailUrl || media.url}
          alt={slot.label}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          <Plus size={14} className="text-ag-black-300" />
        </div>
      )}
    </button>
    {/* Role label below */}
    <span className="text-[8px] font-sourceSans text-ag-black-400 mt-0.5 truncate max-w-[56px] text-center">
      {getImageRoleLabel(lockedModel, slot.role)}
    </span>
    {/* Remove button on hover */}
    {media && onRemove && !isGenerating && (
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black/70 text-ag-black-300 hover:text-white hover:bg-red-600 transition-colors flex items-center justify-center text-[10px] leading-none opacity-0 group-hover:opacity-100 z-10"
      >
        ×
      </button>
    )}
  </div>
);

/**
 * AiResultThumbnail — small clickable thumbnail for a generation result.
 */
const AiResultThumbnail = ({ result, jobId, resultIndex, isActive, onSelect, isSwapping, playingKey, onPlay }) => {
  const { mediaType, url, thumbnailUrl } = result;
  const previewUrl = thumbnailUrl || url;
  const videoRef = React.useRef(null);
  const audioRef = React.useRef(null);

  const thumbKey = `${jobId}-${resultIndex}`;
  const isPlaying = playingKey === thumbKey;
  const isPlayable = mediaType === 'video' || mediaType === 'audio';

  React.useEffect(() => {
    const mediaEl = mediaType === 'video' ? videoRef.current : audioRef.current;
    if (!mediaEl) return;
    if (isPlaying) mediaEl.play().catch(() => {});
    else mediaEl.pause();
  }, [isPlaying, mediaType]);

  const handlePlayToggle = React.useCallback((e) => {
    e.stopPropagation();
    onPlay(isPlaying ? null : thumbKey);
  }, [isPlaying, thumbKey, onPlay]);

  return (
    <button
      onClick={() => !isSwapping && onSelect(jobId, resultIndex)}
      className={`relative rounded-md overflow-hidden border transition-colors flex-shrink-0 ${
        isSwapping ? 'border-ag-green pointer-events-none' :
        isActive ? 'border-ag-green' : 'border-ag-black-600 hover:border-ag-black-400'
      }`}
      style={{ width: 56, height: 56 }}
    >
      {mediaType === 'image' && (
        <img crossOrigin="anonymous" src={previewUrl} alt="Generation" className="w-full h-full object-cover" loading="lazy" />
      )}
      {mediaType === 'video' && (
        <video crossOrigin="anonymous" ref={videoRef} src={url} poster={thumbnailUrl} className="w-full h-full object-cover" muted playsInline onEnded={() => onPlay(null)} />
      )}
      {mediaType === 'audio' && (
        <>
          <div className="w-full h-full flex items-center justify-center bg-ag-black-600">
            <Music size={14} className="text-ag-green" />
          </div>
          <audio crossOrigin="anonymous" ref={audioRef} src={url} onEnded={() => onPlay(null)} />
        </>
      )}
      {isSwapping && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-20">
          <Loader2 size={12} className="animate-spin text-ag-green" />
        </div>
      )}
      {isPlayable && !isSwapping && (
        <div onClick={handlePlayToggle} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center cursor-pointer hover:bg-black/80 transition-colors z-10">
          {isPlaying ? <Pause size={6} className="text-white" /> : <Play size={6} className="text-white ml-[1px]" />}
        </div>
      )}
      {isActive && !isSwapping && (
        <div className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-ag-green flex items-center justify-center z-10">
          <Check size={7} className="text-black" />
        </div>
      )}
    </button>
  );
};

export default AiLiteFields;
